/**
 * Error types for the application
 */
import { analytics } from "utils/analytics";

export enum ErrorType {
  API_ERROR = "API_ERROR",
  NETWORK_ERROR = "NETWORK_ERROR",
  AUTH_ERROR = "AUTH_ERROR",
  PERMISSION_ERROR = "PERMISSION_ERROR",
  VALIDATION_ERROR = "VALIDATION_ERROR",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

/**
 * Application error interface
 */
export interface AppError {
  type: ErrorType;
  message: string;
  originalError?: any;
  code?: string;
  data?: any;
}

/**
 * Creates a standardized application error
 * @param type - The error type
 * @param message - The error message
 * @param originalError - The original error object
 * @param code - Optional error code
 * @param data - Optional additional data
 * @returns A standardized application error
 */
export function createAppError(
  type: ErrorType,
  message: string,
  originalError?: any,
  code?: string,
  data?: any
): AppError {
  return {
    type,
    message,
    originalError,
    code,
    data,
  };
}

/**
 * Handles API errors and converts them to standardized application errors
 * @param error - The error to handle
 * @returns A standardized application error
 */
export function handleApiError(error: any): AppError {
  // Handle axios errors
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    const status = error.response.status;

    if (status === 401) {
      return createAppError(
        ErrorType.AUTH_ERROR,
        error.response.data?.message || "Authentication error",
        error,
        status.toString(),
        error.response.data
      );
    }

    if (status === 403) {
      return createAppError(
        ErrorType.PERMISSION_ERROR,
        error.response.data?.message || "Access forbidden - insufficient permissions",
        error,
        status.toString(),
        error.response.data
      );
    }

    if (status === 400 || status === 422) {
      return createAppError(
        ErrorType.VALIDATION_ERROR,
        error.response.data?.message || "Validation error",
        error,
        status.toString(),
        error.response.data
      );
    }

    return createAppError(
      ErrorType.API_ERROR,
      error.response.data?.message || "API error",
      error,
      status.toString(),
      error.response.data
    );
  } else if (error.request) {
    // The request was made but no response was received
    return createAppError(ErrorType.NETWORK_ERROR, "Network error - no response received", error);
  } else {
    // Something happened in setting up the request that triggered an Error
    return createAppError(ErrorType.UNKNOWN_ERROR, error.message || "Unknown error", error);
  }
}

/**
 * Logs an error to the console with additional context
 * @param error - The error to log
 * @param context - Additional context information
 */
export function logError(error: AppError | any, context?: string): void {
  const appError = error.type ? error : handleApiError(error);

  console.error(`[${appError.type}]${context ? ` [${context}]` : ""}: ${appError.message}`, {
    code: appError.code,
    data: appError.data,
    originalError: appError.originalError,
  });

  // Track the error in analytics
  analytics.trackError(appError.type, {
    message: appError.message,
    code: appError.code,
    context,
  });

  // Here you could also send the error to a monitoring service like Sentry
}

/**
 * Gets a user-friendly error message
 * @param error - The error
 * @returns A user-friendly error message
 */
export function getUserFriendlyErrorMessage(error: AppError | any): string {
  const appError = error.type ? error : handleApiError(error);

  switch (appError.type) {
    case ErrorType.AUTH_ERROR:
      return "Authentication error. Please log in again.";
    case ErrorType.PERMISSION_ERROR:
      return (
        appError.message ||
        "You don't have permission to perform this action. Please contact your administrator."
      );
    case ErrorType.NETWORK_ERROR:
      return "Network error. Please check your internet connection.";
    case ErrorType.VALIDATION_ERROR:
      return appError.message || "Please check your input and try again.";
    case ErrorType.API_ERROR:
      return "Server error. Please try again later.";
    default:
      return "An unexpected error occurred. Please try again.";
  }
}
